import React, { useMemo, useState } from 'react';
import styles from './rtbCampaignOptimize.module.scss';
import i18n from 'i18next';
import { FormikField } from 'components/common/form/field/FormikField';
import { Form } from 'react-bootstrap';
import { formatPriceAny, getCurrencyLabel } from 'helper/CurrencyHelper';
import { getDisplayPrice } from 'helper/CampaignPriceHelper';
import { Dayparts } from 'components/Dayparts/Dayparts';
import _ from 'lodash';
import cx from 'classnames/bind';
import { BidStrategy, L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import { RtbCampaignPlanType } from 'core/rtbCampaign/RtbCampaign';
import { Trans } from 'react-i18next';
import Toggle from 'components/common/Toggle/Toggle';
import { LabelField } from 'components/common/form/field/LabelField';
import { DefaultRtbCampaignManager } from 'core/rtbCampaign/RtbCampaignManager';

const classNames = cx.bind(styles);

const getOptimizationDes = (currency, bidStrategy, optimize, bidPrice) => {
  const optimization = optimize
    ? i18n.t(`optimizationGoal.${optimize.toLowerCase()}`).toLowerCase()
    : i18n.t('optimizationGoal.impressions');
  return i18n.t(`l2ObjectOptimize.labels.${bidStrategy.toLowerCase()}`, {
    optimization,
    bidControl: `${currency} ${formatPriceAny(isNaN(+bidPrice) ? 0 : +bidPrice)}`
  });
};

const renderDayParts = (segmentOnly: boolean) => {
  const DaypartsField = (props) => (
    <Dayparts {...props} segmentOnly={segmentOnly} />
  );
  DaypartsField.displayName = 'DaypartsField';
  return DaypartsField;
};

const RtbCampaignOptimize = ({
  formType,
  currentL2Object,
  optimizeOptions,
  canEditOptimize,
  currency,
  onOptimizeChanged,
  dayPartEnable,
  onDaypartSwitchChanged,
  creativeDeliverTypeOptions,
  outdoorDeliverTypeOptions,
  bidFloor,
  showFrequencyControl,
  optimizeAddonEnable,
  canEditBidStrategy,
  onlyDaypartingOptimize,
  segmentOnlyDaypart
}) => {
  const currencyLabel = useMemo(() => {
    return getCurrencyLabel(currency);
  }, [currency]);

  const [useFrequency, setUseFrequency] = useState(
    !_.isEmpty(currentL2Object.frequency)
  );

  const renderDayPartsWithMode = useMemo(
    () => renderDayParts(!!segmentOnlyDaypart),
    [segmentOnlyDaypart]
  );

  // 出價上限是否被鎖成與合約價一致。唯讀分支與外層的 optimized model 敘述句都要看它，
  // 所以算在這一層。
  const rtbCampaignManager = new DefaultRtbCampaignManager();
  const bidCapEqualOrderPrice =
    !optimizeAddonEnable &&
    rtbCampaignManager.checkOptimizeSameAsPriceModel(
      currentL2Object.priceModel,
      currentL2Object.optimize
    );

  // RKB-2716: 出價上限與 optimized model 敘述句都要顯示 admin 調整過的價格，
  // 兩處必須用同一個值，不然會一頁 266 一頁 280。
  // 只用在唯讀分支 —— 可編輯時欄位值會跟著使用者打字變動，敘述句必須一起動，
  // 釘死在 adminBidPrice 會讓敘述句停在原地。
  const displayBidPrice = getDisplayPrice(
    currentL2Object.adminBidPrice,
    currentL2Object.bidPrice
  );

  // admin 覆寫過的價格視為最終生效值，整個出價區塊不再讓使用者改。
  // truthy 才算調整過（0 / null / undefined 都是未調整），與 getDisplayPrice 同一套語意。
  const hasAdminBidPrice = !!currentL2Object.adminBidPrice;
  const isBidCapReadOnly = bidCapEqualOrderPrice || hasAdminBidPrice;

  const renderBidControl = (formikProps) => {
    const value = formikProps.values[formikProps.name];
    // 純顯示層的強制：被 admin 覆寫時一律呈現為「出價上限」。
    // ⛔ 不得寫回 formik —— bidStrategy 在 formik values 裡，會被整包 spread 進儲存 payload。
    const effectiveStrategy = hasAdminBidPrice
      ? BidStrategy.LOWEST_COST_WITH_BID_CAP
      : value;
    const onBidStrategyChange = (e) => {
      formikProps.setValues((prev) => ({
        ...prev,
        [formikProps.name]: e.target.value,
        bidPrice:
          e.target.value === BidStrategy.LOWEST_COST_WITH_BID_CAP
            ? currentL2Object.orderPrice
            : ''
      }));
    };

    const optimizeSameAsPriceModel =
      rtbCampaignManager.checkOptimizeSameAsPriceModel(
        currentL2Object.priceModel,
        currentL2Object.optimize
      );
    const bidPriceHint = optimizeSameAsPriceModel ? (
      <Trans
        i18nKey='campaign.descriptions.bidPriceRange'
        values={{
          min: currencyLabel + bidFloor,
          orderPrice: currentL2Object.orderPrice
            ? currencyLabel + currentL2Object.orderPrice
            : ''
        }}
        components={{
          1: <span className='text-dark' />,
          3: <span className='text-dark' />
        }}
      />
    ) : bidFloor && bidFloor > 0 ? (
      i18n.t('campaign.descriptions.priceMinimum', {
        min: currencyLabel + bidFloor
      })
    ) : (
      ''
    );
    // 這裡刻意不用傳進來的 formik value：出價上限的 formik value 會被整包送進儲存 payload，
    // 改它使用者一按儲存就會真的把出價上限寫掉，所以顯示值只在這層產生。
    // 與下面 optimized model 敘述句同樣的正規化（見 getOptimizationDes），這樣同一頁的
    // 出價上限不會一個帶千分位一個不帶。用 Number() 而不是一元 + 是因為 displayBidPrice
    // 的型別含 null / undefined，一元 + 會產生 TS18049；兩者對這裡的值行為相同。
    const bidPriceAmount = Number(displayBidPrice);
    const bidPriceFormatter = () =>
      `${currencyLabel} ${formatPriceAny(
        isNaN(bidPriceAmount) ? 0 : bidPriceAmount
      )}`;
    return (
      <div className={styles.bidControl}>
        <Form.Check
          type={'radio'}
          label={i18n.t('l1Object.labels.bidStrategy.lowest_cost_without_cap')}
          value={BidStrategy.LOWEST_COST_WITHOUT_CAP}
          name={'bidStrategy'}
          id={'autoBid'}
          onChange={onBidStrategyChange}
          checked={effectiveStrategy === BidStrategy.LOWEST_COST_WITHOUT_CAP}
          disabled={!canEditBidStrategy || hasAdminBidPrice}
        />
        <Form.Check
          type={'radio'}
          label={i18n.t('l1Object.labels.bidStrategy.lowest_cost_with_bid_cap')}
          value={BidStrategy.LOWEST_COST_WITH_BID_CAP}
          name={'bidStrategy'}
          id={'bidCap'}
          onChange={onBidStrategyChange}
          checked={effectiveStrategy === BidStrategy.LOWEST_COST_WITH_BID_CAP}
          disabled={!canEditBidStrategy || hasAdminBidPrice}
        />
        {effectiveStrategy === BidStrategy.LOWEST_COST_WITH_BID_CAP &&
          (isBidCapReadOnly ? (
            <FormikField.Label name='bidPrice' formatter={bidPriceFormatter} />
          ) : (
            <FormikField.InputGroup
              name='bidPrice'
              prefix={currencyLabel}
              type='number'
              min={0}
              className={styles.bidPrice}
              hint={bidPriceHint}
            />
          ))}
      </div>
    );
  };

  const optimizeLabelFormatter = (optimization) => {
    return i18n.t(`optimizationGoal.${optimization.toLowerCase()}`);
  };

  const renderFrequencyControlInput = (formikProps) => {
    const onMaxFrequencyChange = (e) => {
      formikProps.setFieldValue(
        'frequency.maxFrequency',
        e.target.value < 0 ? 0 : e.target.value
      );
    };
    const onIntervalDaysChange = (e) => {
      formikProps.setFieldValue(
        'frequency.intervalDays',
        e.target.value < 0 ? 0 : e.target.value
      );
    };
    const frequency = formikProps.values[formikProps.name];
    const errorMaxFrequency = _.get(
      formikProps.errors,
      'frequency.maxFrequency'
    );
    const errorintervalDays = _.get(
      formikProps.errors,
      'frequency.intervalDays'
    );
    const maxFrequencyClass = classNames('maxFrequency', {
      error: errorMaxFrequency
    });
    const intervalDaysClass = classNames('intervalDays', {
      error: errorintervalDays
    });
    return (
      <div className={styles.frequencyControl}>
        <div>
          <input
            className={maxFrequencyClass}
            value={frequency.maxFrequency}
            name='frequency.maxFrequency'
            onChange={onMaxFrequencyChange}
            min={1}
            max={20}
            type='number'
          />
          {i18n.t('adSetSetupFlow.mainStep.labels.frequencyCap.event', {
            event: i18n.t('adSetSetupFlow.mainStep.labels.impression'),
            plural: +frequency.maxFrequency > 1 ? 's' : ''
          })}
          <input
            className={intervalDaysClass}
            name='frequency.intervalDays'
            value={frequency.intervalDays}
            onChange={onIntervalDaysChange}
            min={1}
            max={7}
            type='number'
          />
          {i18n.t('adSetSetupFlow.mainStep.labels.frequencyCap.unit', {
            unit: i18n.t('common.units.day'),
            plural: +frequency.intervalDays > 1 ? 's' : ''
          })}
        </div>
        {errorMaxFrequency && (
          <div className='errorTip'>{errorMaxFrequency}</div>
        )}
        {errorintervalDays && (
          <div className='errorTip'>{errorintervalDays}</div>
        )}
      </div>
    );
  };
  const renderFrequencyControlLabel = (frequencyControl) => {
    if (!frequencyControl) {
      return '';
    }
    return i18n.t('adSetSetupFlow.mainStep.labels.frequencyControl', {
      event: i18n.t('adSetSetupFlow.mainStep.labels.impression'),
      eventPlural: +frequencyControl.maxFrequency > 1 ? 's' : '',
      interval_days: frequencyControl.intervalDays,
      max_frequency: frequencyControl.maxFrequency,
      unit: i18n.t('common.units.day'),
      unitPlural: +frequencyControl.intervalDays > 1 ? 's' : ''
    });
  };

  const renderUseFrequency = (formikProps) => {
    const onUseFrequencyChanged = (e) => {
      const use = e.target.checked;
      setUseFrequency(use);
      if (use) {
        formikProps.setFieldValue('frequency', {
          intervalDays: 1,
          maxFrequency: 5
        });
      } else {
        formikProps.setFieldValue('frequency', undefined);
      }
    };

    return (
      <Toggle
        className={styles.useFrequency}
        checked={useFrequency}
        onChange={onUseFrequencyChanged}
      />
    );
  };

  return (
    <fieldset>
      <legend>
        <span>
          {i18n.t('adSetSetupFlow.mainStep.fieldset.optimizationTitle')}
        </span>
      </legend>
      <div className={styles.component}>
        {!onlyDaypartingOptimize && (
          <>
            {canEditOptimize ? (
              <FormikField.Select
                options={optimizeOptions}
                label={i18n.t('adSetSetupFlow.mainStep.field.optimizationGoal')}
                name='optimize'
                simpleValue
                onChange={onOptimizeChanged}
              />
            ) : (
              <FormikField.Label
                label={i18n.t('adSetSetupFlow.mainStep.field.optimizationGoal')}
                name='optimize'
                formatter={optimizeLabelFormatter}
              />
            )}
            <FormikField.Custom
              label={i18n.t('adSetSetupFlow.mainStep.field.bidControl')}
              name='bidStrategy'
              render={renderBidControl}
              hint={
                <div style={{ maxWidth: 500 }}>
                  {getOptimizationDes(
                    currencyLabel,
                    // strategy 決定敘述句用哪個 i18n key，要與 radio 的呈現一致，
                    // 否則會出現「radio 選出價上限、敘述句卻是自動出價」。
                    hasAdminBidPrice
                      ? BidStrategy.LOWEST_COST_WITH_BID_CAP
                      : _.get(currentL2Object, 'bidStrategy'),
                    _.get(currentL2Object, 'optimize', ''),
                    isBidCapReadOnly
                      ? displayBidPrice
                      : currentL2Object.bidPrice
                  )}
                </div>
              }
            />
            <LabelField
              name='billingEvent'
              label={i18n.t('adSetSetupFlow.mainStep.field.billingEvent')}
              className={styles.billingEvent}
              value={
                !currentL2Object.priceModel || !currentL2Object.optimize
                  ? i18n.t('rtbCampaign.billingEvent.unspecified')
                  : currentL2Object.priceModel === RtbCampaignPlanType.RS
                    ? i18n.t(
                        `rtbCampaign.billingEvent.${
                          currentL2Object.priceModel
                        }.${currentL2Object.optimize.toLowerCase()}`
                      )
                    : i18n.t(
                        `rtbCampaign.billingEvent.${currentL2Object.priceModel}`
                      )
              }
            />
            <FormikField.ToggleButton
              name='creativeDeliverType'
              label={i18n.t('campaignInfo.labels.creativeDeliverType')}
              options={creativeDeliverTypeOptions}
            />
            {outdoorDeliverTypeOptions && (
              <FormikField.ToggleButton
                name='outdoorDeliverType'
                label={i18n.t('campaignInfo.labels.outdoorDeliverType')}
                options={outdoorDeliverTypeOptions}
              />
            )}
            {currentL2Object.optimize === L2ObjectOptimizationGoal.CLICKS && (
              <FormikField.Custom
                label={i18n.t(
                  'adSetSetupFlow.mainStep.field.useFrequencyControl'
                )}
                render={renderUseFrequency}
              />
            )}
            {(showFrequencyControl || useFrequency) &&
              !_.isEmpty(currentL2Object.frequency) &&
              (formType === 'create' ? (
                <FormikField.Custom
                  label={i18n.t(
                    'adSetSetupFlow.mainStep.field.frequencyControl'
                  )}
                  name='frequency'
                  render={renderFrequencyControlInput}
                />
              ) : (
                <FormikField.Label
                  label={i18n.t(
                    'adSetSetupFlow.mainStep.field.frequencyControl'
                  )}
                  name='frequency'
                  formatter={renderFrequencyControlLabel}
                />
              ))}
          </>
        )}
        <div className={styles.section}>
          <FormikField.Switch
            label={i18n.t('campaignInfo.labels.daypart')}
            name='dayPart.enabled'
            onChange={onDaypartSwitchChanged}
          />
          {!!dayPartEnable && (
            <FormikField.Custom
              label=''
              name='dayPart'
              render={renderDayPartsWithMode}
            />
          )}
        </div>
      </div>
    </fieldset>
  );
};

export default React.memo(RtbCampaignOptimize);
